import { useState } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { XCircleIcon } from '@heroicons/react/24/solid';
import Avatar from '@/components/Avatar';
import type { SignupEntry } from '@flock-types/signupSheets.types';

interface Props {
  entry: SignupEntry;
  isOwn: boolean;
  isLeader: boolean;
  isPast: boolean;
  /** Own entry → withdraw from the slot; other entries → leader removes. */
  onRemove: () => void;
  onSaveNote: (note: string | null) => Promise<void>;
}

/** Signed-up person: avatar + name + remove + inline note view/edit. */
export default function EntryRow({ entry, isOwn, isLeader, isPast, onRemove, onSaveNote }: Props) {
  const [editing, setEditing] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [saving, setSaving] = useState(false);

  const canEdit = !isPast && (isOwn || isLeader);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveNote(noteText.trim() || null);
      setEditing(false);
    } catch {
      // Parent already toasts; keep the editor open for retry
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-1">
      <div className="flex items-center gap-2">
        <Avatar
          src={entry.user_profile_photo}
          name={entry.user_name}
          sizeClass="h-6 w-6"
          textClass="text-[10px]"
          bgClass="bg-flock-navy-500"
        />
        <span className="text-sm text-flock-navy-600">{entry.user_name}</span>
        {canEdit && (
          <button
            onClick={onRemove}
            title={isOwn ? 'Withdraw' : `Remove ${entry.user_name}`}
            className="p-0.5 text-red-400 transition hover:text-red-600"
          >
            <XCircleIcon className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="ml-8">
        {editing ? (
          <div className="mt-0.5 flex items-center gap-1.5">
            <input
              autoFocus
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
                if (e.key === 'Escape') setEditing(false);
              }}
              maxLength={500}
              placeholder="Add a note..."
              className="flex-1 rounded border border-flock-gray-300 px-2 py-1 text-sm outline-none focus:border-flock-navy-400"
            />
            <button
              onClick={handleSave}
              disabled={saving}
              title="Save note"
              className="p-0.5 text-green-600 hover:text-green-700 disabled:opacity-50"
            >
              <CheckIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setEditing(false)}
              title="Cancel"
              className="p-0.5 text-flock-navy-400 hover:text-flock-navy-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        ) : entry.note ? (
          <button
            onClick={
              canEdit
                ? () => {
                    setNoteText(entry.note || '');
                    setEditing(true);
                  }
                : undefined
            }
            disabled={!canEdit}
            className="text-left text-xs text-flock-navy-400 hover:text-flock-navy-600 disabled:cursor-default disabled:hover:text-flock-navy-400"
          >
            {entry.note}
          </button>
        ) : canEdit ? (
          <button
            onClick={() => {
              setNoteText('');
              setEditing(true);
            }}
            className="text-xs text-flock-navy-400 hover:text-flock-navy-600"
          >
            + add note
          </button>
        ) : null}
      </div>
    </div>
  );
}
